import { ANALYTICS_EVENT_NAMES, type EventName } from "analytics/catalog/events";
import { ScreenKey } from "analytics/catalog/screens";
import { CommonEventProps, buildCommonEventProps } from "analytics/core/commonProps";
import { trackEvent } from "analytics/core/tracker";

export type CampaignStepQuery = "details" | "compensation" | "type" | "instructions";

export type CampaignStepName =
  | "details"
  | "offer"
  | "collaboration_type"
  | "deliverables";

export type CampaignStepNumber = 1 | 2 | 3 | 4;

type CampaignStepMeta = {
  stepQuery: CampaignStepQuery;
  stepName: CampaignStepName;
  stepNumber: CampaignStepNumber;
  screenKey: ScreenKey;
};

export const CAMPAIGN_STEP_META_BY_QUERY: Record<
  CampaignStepQuery,
  CampaignStepMeta
> = {
  details: {
    stepQuery: "details",
    stepName: "details",
    stepNumber: 1,
    screenKey: "campaign_details_step",
  },
  compensation: {
    stepQuery: "compensation",
    stepName: "offer",
    stepNumber: 2,
    screenKey: "campaign_offer_step",
  },
  type: {
    stepQuery: "type",
    stepName: "collaboration_type",
    stepNumber: 3,
    screenKey: "campaign_collaboration_type_step",
  },
  instructions: {
    stepQuery: "instructions",
    stepName: "deliverables",
    stepNumber: 4,
    screenKey: "campaign_deliverables_step",
  },
};

export type CampaignAnalyticsContext = {
  pathname: string;
  search: string;
  businessId?: string | null;
  userId?: string | null;
  userEmail?: string | null;
  campaignId?: string | null;
  planType?: string | null;
  currency?: string | null;
};

type CampaignEventProps = CommonEventProps & {
  step_name: CampaignStepName;
  step_number: CampaignStepNumber;
};

const buildCampaignEventProps = (
  context: CampaignAnalyticsContext,
  stepMeta: CampaignStepMeta,
  extraProps: Record<string, unknown> = {},
): CampaignEventProps & Record<string, unknown> => {
  const commonProps = buildCommonEventProps({
    screenKey: stepMeta.screenKey,
    pathname: context.pathname,
    search: context.search,
    businessId: context.businessId ?? null,
    userId: context.userId ?? null,
    userEmail: context.userEmail ?? null,
    campaignId: context.campaignId ?? null,
    planType: context.planType ?? null,
    currency: context.currency ?? null,
  });

  return {
    ...commonProps,
    step_name: stepMeta.stepName,
    step_number: stepMeta.stepNumber,
    ...extraProps,
  };
};

export const getCampaignStepMeta = (step: CampaignStepQuery): CampaignStepMeta => {
  return CAMPAIGN_STEP_META_BY_QUERY[step];
};

export const trackCampaignStepViewed = (
  context: CampaignAnalyticsContext,
  stepMeta: CampaignStepMeta,
): void => {
  if (stepMeta.stepQuery === "details") {
    trackEvent(
      ANALYTICS_EVENT_NAMES.campaignNewPageViewed,
      buildCampaignEventProps(context, stepMeta, {
        page_name: "campaign_new",
      }),
    );
  }

  const eventNameByStep: Record<CampaignStepQuery, EventName> = {
    details: ANALYTICS_EVENT_NAMES.campaignDetailsStepViewed,
    compensation: ANALYTICS_EVENT_NAMES.campaignOfferStepViewed,
    type: ANALYTICS_EVENT_NAMES.campaignCollaborationTypeStepViewed,
    instructions: ANALYTICS_EVENT_NAMES.campaignDeliverablesStepViewed,
  };

  trackEvent(eventNameByStep[stepMeta.stepQuery], buildCampaignEventProps(context, stepMeta));
};

export const trackCampaignDetailsContinueClicked = (
  context: CampaignAnalyticsContext,
): void => {
  const stepMeta = getCampaignStepMeta("details");
  trackEvent(
    ANALYTICS_EVENT_NAMES.campaignDetailsContinueClicked,
    buildCampaignEventProps(context, stepMeta),
  );
};

export const trackCampaignLocationAddOnMap = (
  context: CampaignAnalyticsContext,
): void => {
  const stepMeta = getCampaignStepMeta("details");
  trackEvent(
    ANALYTICS_EVENT_NAMES.campaignLocationAddOnMap,
    buildCampaignEventProps(context, stepMeta),
  );
};

export const trackCampaignStartDateSelected = (
  context: CampaignAnalyticsContext,
): void => {
  const stepMeta = getCampaignStepMeta("details");
  trackEvent(
    ANALYTICS_EVENT_NAMES.campaignStartDateSelected,
    buildCampaignEventProps(context, stepMeta),
  );
};

export const trackCampaignInfluencerCountChanged = (
  context: CampaignAnalyticsContext,
): void => {
  const stepMeta = getCampaignStepMeta("details");
  trackEvent(
    ANALYTICS_EVENT_NAMES.campaignInfluencerCountChanged,
    buildCampaignEventProps(context, stepMeta),
  );
};

export const trackCampaignCompensationGiftedSelected = (
  context: CampaignAnalyticsContext,
): void => {
  const stepMeta = getCampaignStepMeta("compensation");
  trackEvent(
    ANALYTICS_EVENT_NAMES.campaignCompensationGiftedSelected,
    buildCampaignEventProps(context, stepMeta, {
      compensation_type: "gifted",
    }),
  );
};

export const trackCampaignCompensationPaidSelected = (
  context: CampaignAnalyticsContext,
): void => {
  const stepMeta = getCampaignStepMeta("compensation");
  trackEvent(
    ANALYTICS_EVENT_NAMES.campaignCompensationPaidSelected,
    buildCampaignEventProps(context, stepMeta, {
      compensation_type: "paid",
    }),
  );
};

export const trackCampaignPlatformInstagramSelected = (
  context: CampaignAnalyticsContext,
): void => {
  const stepMeta = getCampaignStepMeta("compensation");
  trackEvent(
    ANALYTICS_EVENT_NAMES.campaignPlatformInstagramSelected,
    buildCampaignEventProps(context, stepMeta),
  );
};

export const trackCampaignPlatformTiktokSelected = (
  context: CampaignAnalyticsContext,
): void => {
  const stepMeta = getCampaignStepMeta("compensation");
  trackEvent(
    ANALYTICS_EVENT_NAMES.campaignPlatformTiktokSelected,
    buildCampaignEventProps(context, stepMeta),
  );
};

export const trackCampaignOfferContinueClicked = (
  context: CampaignAnalyticsContext,
): void => {
  const stepMeta = getCampaignStepMeta("compensation");
  trackEvent(
    ANALYTICS_EVENT_NAMES.campaignOfferContinueClicked,
    buildCampaignEventProps(context, stepMeta),
  );
};

export const trackCampaignCreativeFreedomSelected = (
  context: CampaignAnalyticsContext,
): void => {
  const stepMeta = getCampaignStepMeta("type");
  trackEvent(
    ANALYTICS_EVENT_NAMES.campaignCreativeFreedomSelected,
    buildCampaignEventProps(context, stepMeta),
  );
};

export const trackCampaignPromoOffersSelected = (
  context: CampaignAnalyticsContext,
): void => {
  const stepMeta = getCampaignStepMeta("type");
  trackEvent(
    ANALYTICS_EVENT_NAMES.campaignPromoOffersSelected,
    buildCampaignEventProps(context, stepMeta),
  );
};

export const trackCampaignCollaborationContinueClicked = (
  context: CampaignAnalyticsContext,
): void => {
  const stepMeta = getCampaignStepMeta("type");
  trackEvent(
    ANALYTICS_EVENT_NAMES.campaignCollaborationContinueClicked,
    buildCampaignEventProps(context, stepMeta),
  );
};

export const trackCampaignScheduleClicked = (
  context: CampaignAnalyticsContext,
): void => {
  const stepMeta = getCampaignStepMeta("instructions");
  trackEvent(
    ANALYTICS_EVENT_NAMES.campaignScheduleClicked,
    buildCampaignEventProps(context, stepMeta),
  );
};
